/**
 * subscribe.ts — FormCarry fetch helper
 *
 * POSTs an email signup to the FormCarry public endpoint and returns a typed
 * SubscribeResult union covering success, validation error, and server error.
 *
 * Design decisions:
 *   - FORM_ID is a hard-coded public constant — NOT a secret, NOT an env var (D-10).
 *     FormCarry endpoints are public by design; the ID carries no privilege.
 *   - Success is gated strictly on data.code === 200 (D-05). response.ok alone
 *     is insufficient — a FormCarry body can signal a non-success despite HTTP 200.
 *   - _gotcha is forwarded from the caller (the hidden honeypot field value). An empty
 *     string means a human filled the form; a non-empty value means a bot filled the
 *     hidden field — FormCarry silently drops those submissions server-side (BACK-02, T-03-SP).
 *   - No email-provider key, no build-time env var, no secret of any kind (BACK-03, D-10).
 */

// Public endpoint ID — not a secret (FormCarry by design, D-10)
const FORM_ID = '6NHpuKnjjj1';

const ENDPOINT = `https://formcarry.com/s/${FORM_ID}`;

// Generic server-error fallback message (presentation copy lives in the component)
const SERVER_FALLBACK = 'Something went wrong. Please try again.';

/**
 * Discriminated union type for all subscribe() outcomes.
 * Consumed by NewsletterForm (Plan 02) to drive form state.
 */
export type SubscribeResult =
  | { kind: 'success' }
  | { kind: 'validation'; message: string }
  | { kind: 'server'; message: string };

/**
 * subscribe(email, gotcha) — POST an email signup to the FormCarry endpoint.
 *
 * The `gotcha` parameter is the value of the hidden honeypot field read from the DOM
 * (uncontrolled ref). Empty string means a human submitted the form; a non-empty
 * value means a bot filled the hidden field — FormCarry drops those submissions.
 * The `= ''` default preserves existing callers that omit the argument.
 *
 * Returns a SubscribeResult:
 *   { kind: 'success' }                    — data.code === 200 confirmed
 *   { kind: 'validation'; message: string } — HTTP 422 / data.code === 422
 *   { kind: 'server'; message: string }     — any other status or thrown fetch
 */
export async function subscribe(email: string, gotcha: string = ''): Promise<SubscribeResult> {
  try {
    const response = await fetch(ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      body: JSON.stringify({ email, _gotcha: gotcha }),
    });

    let data: {
      code?: number;
      status?: string;
      message?: string;
      errors?: Record<string, { message: string; rule: string }>;
    } = {};

    try {
      data = (await response.json()) as typeof data;
    } catch {
      // Non-JSON body — leave data as {} and classify by HTTP status below
    }

    // Success: gate strictly on the confirmed FormCarry body code (D-05)
    if (data.code === 200) {
      return { kind: 'success' };
    }

    // Validation error: FormCarry returns HTTP 422 with a human-readable message
    // Also catches 422 responses whose body is not valid JSON (data stays {})
    if (data.code === 422 || response.status === 422) {
      return {
        kind: 'validation',
        message: data.message ?? 'Please enter a valid email address.',
      };
    }

    // All other responses (5xx, unexpected codes) are treated as server errors
    return {
      kind: 'server',
      message: data.message ?? SERVER_FALLBACK,
    };
  } catch {
    // Network failure or thrown fetch — return server result
    return { kind: 'server', message: SERVER_FALLBACK };
  }
}
